/**
 * Referral API Service
 *
 * Centralized API client for all referral-related endpoints.
 * Uses the centralized URL resolver and HTTP utilities.
 *
 * Aligned with backend routes at serverless/api/routes/referral.py
 */

import { buildCloudApiUrl } from './urlResolver';
import { getAuthHeadersFromStorage, handleResponse } from '../utils/httpUtils';

/**
 * Referral data returned from the API
 */
export interface ReferralCode {
  code: string;
  referral_url: string;
  total_referrals: number;
  total_tokens_earned: number;
}

/**
 * Recent referral entry
 */
export interface RecentReferral {
  email: string;
  date: string;
  tokens_earned: number;
}

/**
 * Detailed referral statistics
 */
export interface ReferralStats extends ReferralCode {
  recent_referrals: RecentReferral[];
  referral_limit: number | null;
  referrals_remaining: number | null;
}

/**
 * Apply referral response
 */
export interface ApplyReferralResponse {
  tokens_granted: number;
  message: string;
}

/**
 * Referral API endpoints
 * Aligned with backend router at /api/v1/referral
 */
const ENDPOINTS = {
  code: '/api/v1/referral/code',
  stats: '/api/v1/referral/stats',
  apply: '/api/v1/referral/apply',
} as const;

/**
 * Get the user's referral code (creates one if doesn't exist)
 * Requires authentication
 */
export async function getReferralCode(): Promise<ReferralCode> {
  const response = await fetch(buildCloudApiUrl(ENDPOINTS.code), {
    method: 'GET',
    headers: await getAuthHeadersFromStorage(),
  });
  return handleResponse<ReferralCode>(response);
}

/**
 * Get detailed referral statistics
 * Includes recent referrals and remaining limit
 * Requires authentication
 */
export async function getReferralStats(): Promise<ReferralStats> {
  const response = await fetch(buildCloudApiUrl(ENDPOINTS.stats), {
    method: 'GET',
    headers: await getAuthHeadersFromStorage(),
  });
  return handleResponse<ReferralStats>(response);
}

/**
 * Apply a referral code
 * Both the applicant and the code owner receive bonus tokens
 * Requires authentication
 *
 * @param referralCode - The referral code to apply
 */
export async function applyReferralCode(referralCode: string): Promise<ApplyReferralResponse> {
  const response = await fetch(buildCloudApiUrl(ENDPOINTS.apply), {
    method: 'POST',
    headers: await getAuthHeadersFromStorage(),
    body: JSON.stringify({ referral_code: referralCode }),
  });
  return handleResponse<ApplyReferralResponse>(response);
}

/**
 * Referral API object for named imports
 */
export const referralApi = {
  getCode: getReferralCode,
  getStats: getReferralStats,
  apply: applyReferralCode,
};
